import { Box, Grid, Tooltip, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { StandardDialog, TextField, Button, ConfirmDialog, SelectField } from "@roc/ui";
import { useLenderPortalStore } from "apps/lender-portal/src/app/hooks/useLenderPortalStore";
import { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { ConfirmationButton } from "@roc/ui";
import { observer } from "mobx-react";
import { formatDate, isNil } from "@roc/feature-utils";
import { Document } from "@roc/feature-types";
import { CustomTaskDialog } from "@roc/feature-general-tasks";
import { AchLinkStatus } from "@roc/feature-plaid";

export type ACHFormProps = {
  open?: boolean;
  onClose?: () => void;
  loan: any;
  document: Document;
};

const when = (iso: string) => formatDate(iso, 'MMM d, yyyy h:mm a');

/** What happened to the loan's newest link. The URL itself is never shown again: only a hash of it is stored. */
const linkStatusText = (link: AchLinkStatus, borrowerName: string) => {
  if (!link) return 'No link has been created yet.';
  const who = borrowerName ?? 'the borrower';
  switch (link.status) {
    case 'ACTIVE':
      return `The link for ${who} was created on ${when(link.createdDate)} and expires on ${when(link.expiresAt)}. `
        + (link.lastOpenedAt ? `Last opened on ${when(link.lastOpenedAt)}.` : 'It has not been opened yet.')
        // Links created before links could be copied again have no stored copy.
        + (link.canCopy ? '' : ' This link can\'t be copied again; create a new link to share it.');
    case 'LOCKED':
      return `The link for ${who} is locked after too many wrong codes. Create a new link to continue.`;
    case 'COMPLETED':
      return `${who} completed the ACH form on ${when(link.completedAt)}.`;
    case 'EXPIRED':
      return `The last link for ${who} expired on ${when(link.expiresAt)}.`;
    case 'REVOKED':
      return `The last link for ${who} was cancelled.`;
    default:
      return '';
  }
};

/** Info while the link is open, success once signed, warning once it can no longer be used. */
const alertSeverity = (link: AchLinkStatus): 'info' | 'success' | 'warning' => {
  if (link?.status === 'COMPLETED') return 'success';
  if (link?.status === 'EXPIRED' || link?.status === 'REVOKED' || link?.status === 'LOCKED') return 'warning';
  return 'info';
};

/** Shown before Create Link or Send Email replaces a link that is still open. */
const ReplaceWarning = ({ link, borrowerName, action }: { link: AchLinkStatus; borrowerName?: string; action: 'create' | 'email' }) => {
  const who = borrowerName ?? 'Your borrower';
  return (
    <Box>
      <Typography gutterBottom>
        {who} already has an open link, created on {when(link.createdDate)}
        {link.lastOpenedAt ? ` and last opened on ${when(link.lastOpenedAt)}` : ''}.
      </Typography>
      <Typography gutterBottom>
        <strong>
          {action === 'email' ? 'Sending a new email' : 'Creating a new link'} cancels that link right away. The link
          {borrowerName ? ` ${borrowerName}` : ' your borrower'} already has will stop working
          {link.lastOpenedAt ? ', including if they are filling in the form right now' : ''}.
        </strong>
      </Typography>
      <Typography color="textSecondary">
        To share the same link again, go back and use Copy link instead.
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  instruction: {
    fontWeight: 600,
  },
  underline: {
    fontWeight: 600,
    textDecorationLine: 'underline',
  },
  tooltip: {
    whiteSpace: 'pre-line',
    fontSize: theme.typography.pxToRem(14),
    maxWidth: '200px',
  },
}));

export const ACHFormDialog = observer((props: ACHFormProps) => {
  const { achFormStore } = useLenderPortalStore();
  // Opening the dialog only reads: a link is created when the lender asks for one, since every new link cancels the
  // one the borrower already has.
  useEffect(() => {
    if (props.open && !isNil(props.loan)) {
      achFormStore.reset();
      achFormStore.getLoanACHData(props.loan.loanId, props.loan);
      achFormStore.fetchACHLinkStatus(props.loan.loanId);
    }
  }, [props.open, props.loan]);

  const linkBorrowerName = () => {
    const borrowerId = achFormStore.achLinkStatus?.borrowerId;
    const borrower = props.loan?.borrowers?.find(b => b.borrowerId === borrowerId);
    return borrower?.fullName;
  };
  const completed = achFormStore.achLinkStatus?.status === 'COMPLETED';
  const cannotIssue = isNil(achFormStore.selectedBorrower) || achFormStore.issuingLink;

  const classes = useStyles();

  const linkUrlField = achFormStore.achUrl && (
    <Grid item container justifyContent="flex-start" spacing={2}>
      <Grid item xs={12}>
        <TextField
          testId="achUrl"
          variant="outlined"
          value={achFormStore.achUrl}
          helperText="You can copy this link again any time while it's open, using Copy link above."
          fullWidth
          disabled
        />
      </Grid>
    </Grid>
  );

  const getContent = () => {
    return (
      <>
        <Grid container justifyContent="center">
          {(
            <Grid
              alignItems="center"
              container
              item
              justifyContent="center"
              xs={12}
            >
              <Grid item container xs={11} spacing={2}>
                <Grid item container alignItems="center" spacing={2}>
                  <Grid item xs={12} container spacing={2}>
                    <Grid item xs={12}>
                      <Typography>
                        To facilitate a smooth and efficient closing, an authorized signatory borrower must complete the ACH consent
                        process as part of underwriting. This should take your borrower no more than 5 minutes.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography>
                        The consent process is accessed through a link shared with your borrower. You can copy
                        and send the link to your borrower manually, or you can easily send the link to your
                        borrower in an email via the portal.
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {achFormStore.borrowerOptions.length === 0 &&
                  < Grid item xs={12}>
                    <Typography color="error">
                      Please make sure there is at least one authorized signatory borrower for this action.
                    </Typography>
                  </Grid>
                }
                {achFormStore.borrowerOptions.length > 0 &&
                  <>
                    {achFormStore.borrowerOptions.length === 1 ? (
                      <>
                        <Grid item lg={12} >
                          <TextField
                            testId="selectedBorrower"
                            variant="outlined"
                            standaloneLabel
                            label="Authorized Signatory Borrower"
                            value={achFormStore.currentAuthSignatoryBorrower.fullName}
                            disabled={true}
                            fullWidth
                          />
                        </Grid>
                        <Grid item lg={12} >
                          <TextField
                            testId="selectedBorrower"
                            variant="outlined"
                            standaloneLabel
                            value={achFormStore.currentAuthSignatoryBorrower.emailAddress}
                            disabled={true}
                            fullWidth
                          />
                        </Grid>

                      </>
                    ) : (
                      <>
                        <Grid item xs={12} sm>
                          <SelectField
                            standaloneLabel
                            name={'selectedBorrower'}
                            value={achFormStore.selectedBorrower}
                            options={achFormStore.borrowerOptions}
                            onChange={value => achFormStore.setSelectedBorrower(value)}
                            variant="outlined"
                            fullWidth
                            shrink
                            required
                            testId="selectedBorrower"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Alert
                        severity={alertSeverity(achFormStore.achLinkStatus)}
                        action={achFormStore.achLinkStatus?.canCopy && (
                          <Button
                            testId="copyACHLink"
                            color="inherit"
                            size="small"
                            onClick={() => achFormStore.copyACHLink(props.loan.loanId)}
                            disabled={achFormStore.issuingLink}
                          >
                            COPY LINK
                          </Button>
                        )}
                      >
                        {linkStatusText(achFormStore.achLinkStatus, linkBorrowerName())}
                      </Alert>
                    </Grid>
                    {linkUrlField}
                    {!completed &&
                      <Grid item container justifyContent="flex-start" spacing={2}>
                        <Grid item xs={12}>
                          <Typography className={classes.instruction}>
                            Select Link Delivery Method
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sm={3}>
                          <Tooltip title="Create a new link, then copy it and share it with your borrower manually. Any open link stops working." arrow placement="left" classes={{ tooltip: classes.tooltip, }}>
                            <div>
                              {achFormStore.hasOpenLink ? (
                                <ConfirmationButton
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => achFormStore.getACHLink(props.loan.loanId)}
                                  testId="createACHLink"
                                  disabled={cannotIssue}
                                  confirmDialogProps={{
                                    title: 'Replace the current link?',
                                    body: <ReplaceWarning link={achFormStore.achLinkStatus} borrowerName={linkBorrowerName()} action="create" />,
                                    cancelButtonText: 'Keep current link',
                                    confirmButtonText: 'Create new link'
                                  }}
                                  fullWidth
                                >
                                  CREATE LINK
                                </ConfirmationButton>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => achFormStore.getACHLink(props.loan.loanId)}
                                  testId="createACHLink"
                                  disabled={cannotIssue}
                                  fullWidth
                                >
                                  CREATE LINK
                                </Button>
                              )}
                            </div>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={4} sm={3}>
                          <Tooltip title="Automatically email a new link to your borrower" arrow placement="right" classes={{ tooltip: classes.tooltip, }}>
                            <div>
                              <ConfirmationButton
                                variant="contained"
                                onClick={() => achFormStore.sendACHEmail(props.loan.loanId)}
                                color="primary"
                                testId="sendACHEmail"
                                disabled={cannotIssue}
                                confirmDialogProps={achFormStore.hasOpenLink ? {
                                  title: 'Email a new link?',
                                  body: <ReplaceWarning link={achFormStore.achLinkStatus} borrowerName={linkBorrowerName()} action="email" />,
                                  cancelButtonText: 'Keep current link',
                                  confirmButtonText: 'Send new link'
                                } : {
                                  title: 'Confirmation',
                                  body: 'This will send an email containing the link to the ACH consent process to your borrower.',
                                  cancelButtonText: 'Go Back',
                                  confirmButtonText: 'Ok'
                                }}
                                fullWidth
                              >
                                Send Email
                              </ConfirmationButton>
                            </div>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    }
                  </>
                }
                <>

                </>
              </Grid>
            </Grid>
          )}
        </Grid>
      </>
    );
  }

  return (
    <>
      {props.document?.taskId ? (
        <CustomTaskDialog
          open={props.open}
          title="ACH Consent"
          handleClose={props.onClose}
          dialogContent={getContent()}
          taskId={props.document?.taskId}
        />
      ) : (
        <StandardDialog
          open={props.open}
          title="ACH Consent"
          maxWidth="md"
          handleClose={props.onClose}
          dialogContent={getContent()}
        />
      )}
    </>

  );
});